// iter150-review — Visibility + online-aware polling helper.
//
// Replaces the ad-hoc setInterval pattern across the app so background
// tabs stop making requests. Rules:
//   • Fires `fn` once on mount (if visible + online).
//   • While visible + online: schedules the next tick `ms` later.
//   • On visibilitychange → visible OR on 'online': fires immediately.
//   • While hidden/offline: no requests go out. We also skip the next
//     scheduled tick if the tab is still hidden when it fires.
//
// Deliberately *not* used by countdown-only timers (CountdownPill,
// OfferCountdownBig, ActivationCountdown, FomoCounter) — those don't
// call the server.
import { useEffect, useRef } from "react";

export function usePolling(fn, ms, enabled = true) {
  const fnRef = useRef(fn);
  fnRef.current = fn;

  useEffect(() => {
    if (!enabled) return undefined;
    let timerId = null;
    let cancelled = false;

    const canFire = () => {
      if (typeof document !== "undefined" && document.visibilityState === "hidden") return false;
      if (typeof navigator !== "undefined" && navigator.onLine === false) return false;
      return true;
    };

    const tick = async () => {
      if (cancelled) return;
      if (canFire()) {
        try { await fnRef.current?.(); } catch { /* swallow — caller logs */ }
      }
      if (!cancelled) {
        timerId = setTimeout(tick, ms);
      }
    };

    const kickNow = () => {
      if (!canFire() || cancelled) return;
      if (timerId) { clearTimeout(timerId); timerId = null; }
      tick();
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible") kickNow();
    };
    const onOnline = () => kickNow();
    const onFocus = () => kickNow();

    tick();

    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", onVisibility);
    }
    if (typeof window !== "undefined") {
      window.addEventListener("online", onOnline);
      window.addEventListener("focus", onFocus);
    }

    return () => {
      cancelled = true;
      if (timerId) clearTimeout(timerId);
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", onVisibility);
      }
      if (typeof window !== "undefined") {
        window.removeEventListener("online", onOnline);
        window.removeEventListener("focus", onFocus);
      }
    };
  }, [ms, enabled]);
}
